import React, { useState, useMemo } from 'react';
import { useEffect } from 'react';
import SafeChart from '../components/common/SafeChart';
import { useCRM } from '../context/CRMContext';
import { useCRMData } from '../context/CRMDataContext';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { formatCurrency } from '../utils/helpers';

const PIPELINE_STAGES_STORAGE_KEY = 'saleswork_pipeline_stages';

const getLocalStorageArray = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getDealId = deal => deal?.deal_id || deal?.id;

const getDealValue = deal => (
  Number(deal?.value || deal?.dealValue || deal?.amount || 0)
);

const getDealStatus = deal => String(deal?.status || '').toLowerCase();

const isActiveDeal = deal => {
  const status = String(deal?.status || '').toLowerCase();

  return (
    deal &&
    deal.archived !== true &&
    deal.deleted !== true &&
    deal.is_deleted !== true &&
    status !== 'arkivert' &&
    status !== 'slettet' &&
    status !== 'deleted' &&
    !isWonDeal(deal)
  );
};

const isActivePipelineDeal = deal => (
  isActiveDeal(deal) && Boolean(deal?.stage_id)
);

const getStageId = stage => stage?.stage_id || stage?.id;

const getStageOrder = (stage, index) => (
  stage?.order ?? stage?.sort_order ?? index
);

const formatStageLabel = name => {
  if (name === 'Følg opp/Ring opp') return 'Følg opp';
  if (name === 'Avtalt (booket) møte') return 'Avtalt møte';
  if (name === 'Vunnet (overføres som kunde)') return 'Vunnet';
  return name;
};

function isWonDeal(deal) {
  return (
    deal?.isCustomer === true ||
    deal?.convertedToCustomer === true ||
    deal?.customerStatus === 'active' ||
    getDealStatus(deal) === 'customer' ||
    getDealStatus(deal) === 'closed_won' ||
    getDealStatus(deal) === 'vunnet' ||
    deal?.status === 'customer' ||
    deal?.status === 'closed_won' ||
    deal?.status === 'Vunnet'
  );
}

const isOpenDeal = deal => (
  isActiveDeal(deal) &&
  Boolean(deal?.stage_id) &&
  !isWonDeal(deal) &&
  deal?.status !== 'Tapt' &&
  deal?.status !== 'lost'
);

const isReportStageDeal = deal => {
  const status = getDealStatus(deal);

  return (
    deal &&
    deal.archived !== true &&
    deal.deleted !== true &&
    deal.is_deleted !== true &&
    status !== 'arkivert' &&
    status !== 'slettet' &&
    status !== 'deleted' &&
    Boolean(deal.stage_id)
  );
};

const getSellerLabel = item => (
  item?.ownerName ||
  item?.owner ||
  item?.sellerName ||
  item?.userName ||
  'Ukjent selger'
);

const Reports = () => {
  const { users, departments, getAccessibleUsers } = useCRM();
  const { deals, activities, pipelineStages } = useCRMData();
  const { currentUser } = useAuth();
  const [reportPipelineStages, setReportPipelineStages] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const storedStages = getLocalStorageArray(PIPELINE_STAGES_STORAGE_KEY);

    if (storedStages.length > 0) {
      setReportPipelineStages(storedStages);
    } else if (Array.isArray(pipelineStages) && pipelineStages.length > 0) {
      setReportPipelineStages(pipelineStages);
    }

    const loadProductionStages = async () => {
      if (import.meta.env.PROD !== true) return;

      const { data, error } = await supabase
        .from('pipeline_stages_1710925200000')
        .select('id,name,sort_order,color')
        .order('sort_order', { ascending: true });

      if (!isMounted) return;

      if (error) {
        console.warn('[REPORTS PIPELINE STAGES] Kunne ikke laste pipeline-steg.', error.message);
        return;
      }

      if (Array.isArray(data) && data.length > 0) {
        setReportPipelineStages(data);
      }
    };

    loadProductionStages();

    return () => {
      isMounted = false;
    };
  }, [pipelineStages]);

  const reportStages = useMemo(() => {
    const sourceStages = reportPipelineStages.length > 0
      ? reportPipelineStages
      : Array.isArray(pipelineStages)
        ? pipelineStages
        : [];

    return sourceStages
      .map((stage, index) => ({
        ...stage,
        id: getStageId(stage),
        order: getStageOrder(stage, index)
      }))
      .filter(stage => stage.id && stage.name)
      .sort((a, b) => a.order - b.order);
  }, [reportPipelineStages, pipelineStages]);

  // Bruker kan kun velge avdelinger de har tilgang til
  const accessibleUserIds = getAccessibleUsers(currentUser);
  
  // Finn hvilke avdelinger disse brukerne tilhører for å populere filteret
  const relevantDepartments = departments.filter(dept => {
    // Admin ser alt
    if (currentUser.role === 'Admin') return true;
    // Andre ser kun hvis de eller deres underordnede er i avdelingen
    const usersInDept = users.filter(u => u.department_id === dept.department_id);
    return usersInDept.some(u => accessibleUserIds.includes(u.user_id));
  });

  const [selectedDeptId, setSelectedDeptId] = useState('all');

  const filteredData = useMemo(() => {
    let targetUserIds = [];

    if (selectedDeptId === 'all') {
      targetUserIds = accessibleUserIds;
    } else {
      // Hvis en spesifikk avdeling er valgt, finn brukere i den avdelingen (+ underavdelinger hvis aktuelt)
      // For enkelhets skyld i filteret, tar vi direkte medlemmer av valgt avdeling
      // I en full implementasjon ville vi brukt getAccessibleUsers logikken rekursivt her også
      targetUserIds = users
        .filter(u => u.department_id === selectedDeptId && accessibleUserIds.includes(u.user_id))
        .map(u => u.user_id);
    }

    const filteredDeals = deals.filter(d => d && targetUserIds.includes(d.owner_user_id));
    const filteredActivities = activities.filter(a => a && targetUserIds.includes(a.user_id));

    const demoDeals = getLocalStorageArray('saleswork_demo_deals');
    const demoSalesEvents = getLocalStorageArray('saleswork_demo_sales_events');
    const dealsById = new Map();

    [...(Array.isArray(deals) ? deals : []), ...demoDeals].forEach(deal => {
      const dealId = getDealId(deal);
      if (dealId) dealsById.set(String(dealId), deal);
    });

    const hasUserFilter = targetUserIds.length > 0;
    const belongsToSelectedUsers = item => (
      !hasUserFilter ||
      targetUserIds.includes(item?.owner_user_id) ||
      targetUserIds.includes(item?.user_id)
    );

    const sourceDeals = Array.from(dealsById.values());
    const reportDeals = sourceDeals.filter(deal => deal && belongsToSelectedUsers(deal));
    const activeFilteredDeals = filteredDeals.filter(isActiveDeal);
    const activeReportDeals = reportDeals.filter(isActiveDeal);
    const reportStageDeals = reportDeals.filter(isReportStageDeal);
    const closedWonEvents = demoSalesEvents
      .filter(event => event?.type === 'closed_won')
      .filter(event => belongsToSelectedUsers(event));
    const reportActivities = filteredActivities.length > 0
      ? filteredActivities
      : activeReportDeals.flatMap(deal => [
        ...(Array.isArray(deal.logs) ? deal.logs : []),
        ...(Array.isArray(deal.activities) ? deal.activities : [])
      ]);

    const totalPipeline = activeFilteredDeals.reduce((sum, d) => sum + (d.status === 'Åpen' && Boolean(d.stage_id) ? (d.value || 0) : 0), 0);
    const wonRevenue = reportDeals
      .filter(isWonDeal)
      .reduce((sum, d) => sum + getDealValue(d), 0);
    const winCount = reportDeals.filter(isWonDeal).length;
    const totalCount = winCount + reportDeals.filter(d => d.status === 'Tapt').length;
    const winRate = totalCount > 0 ? Math.round((winCount / totalCount) * 100) : 0;

    const openPipelineValue = activeReportDeals
      .filter(isOpenDeal)
      .reduce((sum, deal) => sum + getDealValue(deal), 0);

    const wonDeals = reportDeals.filter(isWonDeal);
    const eventRevenue = closedWonEvents.reduce(
      (sum, event) => sum + Number(event?.value || 0),
      0
    );
    const reportWonRevenue = wonDeals.length > 0
      ? wonRevenue
      : eventRevenue;

    const reportWinCount = wonDeals.length > 0 ? wonDeals.length : closedWonEvents.length;
    const lostCount = activeReportDeals.filter(
      deal => deal.status === 'Tapt' || deal.status === 'lost'
    ).length;
    const reportTotalCount = reportWinCount + lostCount;
    const reportWinRate = reportTotalCount > 0
      ? Math.round((reportWinCount / reportTotalCount) * 100)
      : 0;

    const fallbackActivityCount = activeReportDeals.reduce((sum, deal) => (
      sum +
      (Array.isArray(deal.logs) ? deal.logs.length : 0) +
      (Array.isArray(deal.activities) ? deal.activities.length : 0)
    ), 0);

    const activityCount = reportActivities.length ||
      fallbackActivityCount ||
      closedWonEvents.length;

    const revenueBySeller = closedWonEvents.reduce((acc, event) => {
      const seller = getSellerLabel(event);

      if (!acc[seller]) {
        acc[seller] = { seller, revenue: 0, wonCount: 0 };
      }

      acc[seller].revenue += Number(event?.value || 0);
      acc[seller].wonCount += 1;

      return acc;
    }, {});

    if (closedWonEvents.length === 0) {
      wonDeals.forEach(deal => {
        const seller = getSellerLabel(deal);

        if (!revenueBySeller[seller]) {
          revenueBySeller[seller] = { seller, revenue: 0, wonCount: 0 };
        }

        revenueBySeller[seller].revenue += getDealValue(deal);
        revenueBySeller[seller].wonCount += 1;
      });
    }

    return {
      deals: activeReportDeals,
      stageDeals: reportStageDeals,
      activities: reportActivities,
      salesEvents: closedWonEvents,
      revenueBySeller: Object.values(revenueBySeller),
      totalPipeline: openPipelineValue,
      wonRevenue: reportWonRevenue,
      winRate: reportWinRate,
      activityCount
    };
  }, [selectedDeptId, deals, activities, users, accessibleUserIds]);

  const pipelinePhaseData = reportStages.map(stage => {
    const stageIds = [stage.stage_id, stage.id].filter(Boolean);

    return {
      id: stage.id,
      label: stage.name,
      order: stage.order,
      count: filteredData.stageDeals
        .filter(deal => stageIds.includes(deal.stage_id))
        .length
    };
  });

  const pipelineOption = {
    title: { text: 'Pipeline per Fase', left: 'center' },
    tooltip: {
      trigger: 'item',
      formatter: params => {
        const item = pipelinePhaseData[params.dataIndex];
        return `${item?.label || params.name}: ${params.value}`;
      }
    },
    grid: {
      bottom: 70
    },
    xAxis: {
      type: 'category',
      data: pipelinePhaseData.map(item => formatStageLabel(item.label)),
      axisLabel: {
        interval: 0,
        rotate: -25,
        margin: 18,
        hideOverlap: false
      }
    },
    yAxis: { type: 'value' },
    series: [{
      data: pipelinePhaseData.map(item => item.count),
      type: 'bar',
      itemStyle: { color: '#3b82f6' }
    }]
  };

  const revenueBreakdownOption = useMemo(() => {
    // Viser breakdown basert på brukerne i utvalget
    const activeUsersInView = users.filter(u => accessibleUserIds.includes(u.user_id));
    
    // Hvis mange brukere, grupper på avdeling, ellers vis per bruker
    const showByDept = activeUsersInView.length > 10;
    
    let xData = [];
    let seriesData = [];

    if (showByDept) {
       xData = relevantDepartments.map(d => d.name);
       seriesData = relevantDepartments.map(d => {
         const deptUsers = users.filter(u => u.department_id === d.department_id).map(u => u.user_id);
         return filteredData.deals
           .filter(deal => deptUsers.includes(deal.owner_user_id) && deal.status === 'Vunnet')
           .reduce((sum, deal) => sum + (deal.value || 0), 0);
       });
    } else {
       // Filter users that are actually in the filtered dataset logic to avoid showing everyone
       const relevantUsers = users.filter(u => {
          if (selectedDeptId === 'all') return accessibleUserIds.includes(u.user_id);
          return u.department_id === selectedDeptId;
       });
       
       xData = relevantUsers.map(u => u.name);
       seriesData = relevantUsers.map(u => {
         return filteredData.deals
           .filter(d => d.owner_user_id === u.user_id && d.status === 'Vunnet')
           .reduce((sum, d) => sum + (d.value || 0), 0);
       });
    }

    if (xData.length === 0 && filteredData.revenueBySeller.length > 0) {
      xData = filteredData.revenueBySeller.map(row => row.seller);
      seriesData = filteredData.revenueBySeller.map(row => row.revenue);
    }

    return {
      title: { text: showByDept ? 'Omsetning per Avdeling' : 'Omsetning per Selger', left: 'center' },
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: xData },
      yAxis: { type: 'value' },
      series: [{ data: seriesData, type: 'bar', itemStyle: { color: '#10b981' } }]
    };
  }, [filteredData, users, accessibleUserIds, relevantDepartments, selectedDeptId]);

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Rapportering & Analyse</h1>
          <p className="text-slate-500 font-medium">
            Viser data for: <span className="text-blue-600 font-bold">{currentUser.role}</span> (Nivå {currentUser.role === 'Admin' ? 'Konsern' : currentUser.role})
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center bg-white border border-slate-200 rounded-xl px-4 py-2 shadow-sm">
            <SafeIcon icon="🏢" className="text-slate-400 mr-2" />
            <select
              value={selectedDeptId}
              onChange={(e) => setSelectedDeptId(e.target.value)}
              className="bg-transparent border-none focus:ring-0 text-sm font-bold text-slate-700 outline-none cursor-pointer"
            >
              <option value="all">Alle mine enheter</option>
              {relevantDepartments.map(d => (
                <option key={d.department_id} value={d.department_id}>{d.name} ({d.type})</option>
              ))}
            </select>
          </div>
          <button className="flex items-center px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-black text-slate-700 hover:bg-slate-50 transition-all">
            <SafeIcon icon="⬇️" className="mr-2" /> Eksport PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'Pipeline Verdi (Åpen)', value: formatCurrency(filteredData.totalPipeline), color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Omsetning (Vunnet)', value: formatCurrency(filteredData.wonRevenue), color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Win Rate', value: `${filteredData.winRate}%`, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'Aktiviteter', value: filteredData.activityCount, color: 'text-slate-900', bg: 'bg-slate-50' }
        ].map((kpi, i) => (
          <div key={i} className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{kpi.label}</p>
            <p className={`text-3xl font-black mt-2 ${kpi.color}`}>{kpi.value}</p>
            <div className={`h-1 w-12 rounded-full mt-4 ${kpi.bg.replace('bg-', 'bg-')}`}></div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-[2.5rem] shadow-sm border border-slate-200">
          <SafeChart option={revenueBreakdownOption} style={{ height: '350px' }} />
        </div>
        <div className="bg-white p-6 rounded-[2.5rem] shadow-sm border border-slate-200">
          <SafeChart option={pipelineOption} style={{ height: '400px' }} />
        </div>
      </div>
    </div>
  );
};

export default Reports;